import React, { useState } from 'react';
import { Search, Plus, Upload } from 'lucide-react';
import { useCustomers } from '../../hooks/useCustomers';
import { formatters } from '../../utils/formatters';
import LoadingSpinner from '../common/LoadingSpinner';
import Modal from '../common/Modal';
import AddPointsForm from './AddPointsForm';
import PointsCsvImportModal from './PointsCsvImportModal';
import { useAuth } from '../../contexts/AuthContext';

const PointsSearch = () => {
  const { user } = useAuth();
  const { customers, loading, loadCustomers } = useCustomers();
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddPoints, setShowAddPoints] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showCsvImport, setShowCsvImport] = useState(false);

  const filteredCustomers = customers.filter(customer => {
    // Se não há termo de busca, mostrar todos
    if (!searchTerm.trim()) {
      return true;
    }
    
    const searchLower = searchTerm.toLowerCase();
    const cleanSearchTerm = searchTerm.replace(/\D/g, '');
    const cleanCustomerDoc = (customer.cpf || '').replace(/\D/g, '');
    
    // Busca por nome
    if (customer.name.toLowerCase().includes(searchLower)) {
      return true;
    }
    
    // Busca por documento limpo (só números)
    if (cleanSearchTerm && cleanCustomerDoc.includes(cleanSearchTerm)) {
      return true;
    }
    
    // Busca por documento formatado
    try {
      const formattedDoc = formatters.formatCpfOrCnpj(customer.cpf);
      if (formattedDoc.toLowerCase().includes(searchLower)) {
        return true;
      }
    } catch (error) {
      // Ignora erros de formatação
    }
    
    // Busca por email ou telefone se disponível
    if (customer.email && customer.email.toLowerCase().includes(searchLower)) {
      return true;
    }
    
    if (customer.phone && customer.phone.includes(cleanSearchTerm)) {
      return true;
    }
    
    return false;
  });

  const handleAddPoints = (customer) => {
    setSelectedCustomer(customer);
    setShowAddPoints(true);
  };

  const handleCloseForm = () => {
    setShowAddPoints(false);
    setSelectedCustomer(null);
  };

  const handleImportComplete = async (results) => {
    if (results.processed > 0) {
      // Recarregar a lista de clientes após importação com um pequeno delay
      try {
        // Aguardar um pouco para garantir que as operações do Firebase foram finalizadas
        setTimeout(async () => {
          await loadCustomers();
        }, 1000);
      } catch (error) {
        console.error('Erro ao recarregar clientes após importação:', error);
      }
      
      // Mostrar mensagem de sucesso
      if (results.added > 0 && results.redeemed > 0) {
        alert(`Importação concluída! ${results.added} adições e ${results.redeemed} resgates processados.`);
      } else if (results.added > 0) {
        alert(`Importação concluída! ${results.added} adições de pontos processadas.`);
      } else if (results.redeemed > 0) {
        alert(`Importação concluída! ${results.redeemed} resgates de pontos processados.`);
      }
    }
    
    setShowCsvImport(false);
  };

  if (loading) {
    return <LoadingSpinner size="lg" className="py-20" />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-800">Consultar Pontos</h2>
        <button
          onClick={() => setShowCsvImport(true)}
          className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2"
        >
          <Upload className="w-5 h-5" />
          <span>Importar Pontos</span>
        </button>
      </div>
      
      <div className="bg-white rounded-xl p-6 shadow-sm border">
        <div className="flex items-center space-x-4 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Buscar por nome ou CPF/CNPJ..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        <div className="text-sm text-gray-600 mb-4">
          Total de clientes: {customers.length} | 
          Resultados da busca: {filteredCustomers.length}
        </div>

        <div className="space-y-3">
          {filteredCustomers.map(customer => (
            <div key={customer.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900">{customer.name}</p>
                    <p className="text-sm text-gray-600">
                      {formatters.formatCpfOrCnpj(customer.cpf)}
                      {customer.cpf && (
                        <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                          {customer.cpf.length === 11 ? 'CPF' : customer.cpf.length === 14 ? 'CNPJ' : 'Doc'}
                        </span>
                      )}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-gray-900">{customer.points} pontos</p>
                    <p className="text-sm text-gray-600">Saldo atual</p>
                  </div>
                </div>
              </div>
              <button
                onClick={() => handleAddPoints(customer)}
                className="ml-4 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2"
              >
                <Plus className="w-4 h-4" />
                <span>Adicionar Pontos</span>
              </button>
            </div>
          ))}
          
          {filteredCustomers.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              {searchTerm ? 'Nenhum cliente encontrado' : 'Nenhum cliente cadastrado ainda'}
            </div>
          )}
        </div>
      </div>

      <Modal
        isOpen={showAddPoints}
        onClose={handleCloseForm}
        title="Adicionar Pontos"
      >
        {selectedCustomer && (
          <AddPointsForm
            customer={selectedCustomer}
            onSuccess={handleCloseForm}
            onCancel={handleCloseForm}
          />
        )}
      </Modal>

      <PointsCsvImportModal
        isOpen={showCsvImport}
        onClose={() => setShowCsvImport(false)}
        companyId={user?.uid}
        onImportComplete={handleImportComplete}
      />
    </div>
  );
};

export default PointsSearch;